// Податковий профіль платника (docs/taxpayer-profiles-plan.md, TP-00–TP-05): хто платник (юрособа / ФОП), система
// оподаткування, група ЄП, ПДВ — з датою чинності. Підтверджений профіль задає, які розділи, синхронізації й форми
// стосуються компанії. «Невідомо» — це відсутність підтвердженого профілю (тоді — режим сумісності: усе як раніше).
// Відомості ДПС (облікова картка платника) — лише кандидат із джерелом кожного поля; підтверджує людина.

export const ENTITY = { legal_person: "Юридична особа", sole_trader: "ФОП (фізична особа — підприємець)" };
export const SYSTEM = { general: "Загальна система", single: "Спрощена система (єдиний податок)" };
export const VAT = { registered: "Платник ПДВ", not_registered: "Не платник ПДВ" };
export const GROUPS = [1, 2, 3, 4];

const ISO = /^\d{4}-\d{2}-\d{2}$/;

// Допустимі комбінації. Повертає [{ field, message }] — порожньо, якщо профіль можна підтвердити
export function validateProfile(p = {}) {
  const errors = [];
  const bad = (field, message) => errors.push({ field, message });
  if (!ENTITY[p.entity_kind]) bad("entity_kind", "Оберіть, хто платник: юридична особа чи ФОП");
  if (!SYSTEM[p.tax_system]) bad("tax_system", "Оберіть систему оподаткування");
  if (!VAT[p.vat_status]) bad("vat_status", "Вкажіть, чи є платником ПДВ");
  const g = p.single_tax_group == null || p.single_tax_group === "" ? null : Number(p.single_tax_group);
  if (p.tax_system === "single") {
    if (!GROUPS.includes(g)) bad("single_tax_group", "Вкажіть групу єдиного податку (1–4)");
    else if (p.entity_kind === "legal_person" && ![3, 4].includes(g)) bad("single_tax_group", "Юридична особа може бути лише в 3 або 4 групі єдиного податку");
    else if ([1, 2].includes(g) && p.vat_status === "registered") bad("vat_status", "Платники 1 і 2 груп не можуть бути платниками ПДВ");
  } else if (g != null) bad("single_tax_group", "Група вказується лише для спрощеної системи");
  if (!ISO.test(String(p.valid_from || "")) || Number.isNaN(Date.parse(p.valid_from))) bad("valid_from", "Вкажіть дату, з якої діє профіль");
  return errors;
}

// Профіль на дату: записи — [{ valid_from, valid_to (виключно, null — відкрито), superseded_at, … }]
export function profileAt(records, date) {
  const d = String(date).slice(0, 10);
  return records.find((r) => !r.superseded_at && r.valid_from <= d && (!r.valid_to || d < r.valid_to)) || null;
}

const dmy = (s) => { const m = String(s ?? "").match(/^(\d{2})\.(\d{2})\.(\d{4})/); if (m) return `${m[3]}-${m[2]}-${m[1]}`; const i = String(s ?? "").match(/^(\d{4}-\d{2}-\d{2})/); return i ? i[1] : null; };

// Кандидат профілю з облікової картки платника (блоки кабінету ДПС). Картка — поточний стан, не історія
export function candidateFromCard(blocks, { fetchedAt = null } = {}) {
  if (!Array.isArray(blocks) || !blocks.length) return null;
  const B = Object.fromEntries(blocks.map((b) => [Number(b.idGroup), b]));
  const v = (g, k) => { const x = B[g]?.values?.[k]; return x == null || String(x).trim() === "" ? null : String(x).trim(); };
  const src = (label) => ({ source: "payer_card", label, fetchedAt });
  const tin = (v(1, "TIN") || "").replace(/\D/g, "");
  const out = { evidence: {} };
  if (tin.length === 8) { out.entity_kind = "legal_person"; out.evidence.entity_kind = { ...src("Податковий номер — 8 цифр (ЄДРПОУ)") }; }
  else if (tin.length === 10) { out.entity_kind = "sole_trader"; out.evidence.entity_kind = { ...src("Податковий номер — 10 цифр (РНОКПП); підприємницький статус підтвердьте") }; }
  const vatFrom = dmy(v(5, "DAT_REESTR")), vatAnul = dmy(v(5, "DAT_ANUL"));
  if (v(5, "KOD_PDV") && !vatAnul) { out.vat_status = "registered"; out.evidence.vat_status = { ...src(`Зареєстровано платником ПДВ${vatFrom ? ` з ${vatFrom}` : ""}`), since: vatFrom }; }
  else { out.vat_status = "not_registered"; out.evidence.vat_status = { ...src(vatAnul ? `Реєстрацію платником ПДВ анульовано ${vatAnul}` : "Реєстрації платником ПДВ у картці немає"), since: vatAnul }; }
  const epFrom = dmy(v(6, "DATA_N")), epAnul = dmy(v(6, "DAT_ANUL")), grp = Number(v(6, "GRUP")) || null;
  if (epFrom && !epAnul) {
    out.tax_system = "single"; out.single_tax_group = GROUPS.includes(grp) ? grp : null;
    out.evidence.tax_system = { ...src(`Спрощена система з ${epFrom}${grp ? `, ${grp} група` : ""}${v(6, "STAVKA") ? `, ставка ${v(6, "STAVKA")}` : ""}`), since: epFrom };
  } else {
    out.tax_system = "general"; out.single_tax_group = null;
    out.evidence.tax_system = { ...src(epAnul ? `Єдиний податок анульовано ${epAnul}` : "Реєстрації платником єдиного податку в картці немає — загальна система"), since: epAnul };
  }
  if (v(4, "C_NONPR")) out.evidence.nonprofit = { ...src("Ознака неприбутковості в картці — цей сценарій eCabinet ще не підтримує") };
  // Пропозиція дати чинності: найпізніша з дат подій, що визначають профіль (зазвичай — реєстрація ПДВ чи перехід на ЄП)
  const dates = [out.evidence.vat_status.since, out.evidence.tax_system.since, dmy(v(2, "D_REG_STI"))].filter(Boolean).sort();
  out.valid_from = dates.at(-1) || null;
  return out;
}

// Що стосується компанії з цим профілем. profile = null — режим сумісності (профіль ще не підтверджено): усе як раніше.
// sections — розділи меню: applicable (стосується), reason (чому ні). sync — розділи кабінету для дефолтного синку.
// notYet — що профіль вимагає, але eCabinet ще не вміє (показуємо чесно, а не порожні форми)
const ALWAYS = ["overview", "reports", "requests", "correspondence", "opp", "budget", "customs", "reference", "params", "card", "partners", "prro"];
const VAT_ONLY = { erpn: "ПН/РК ведуть лише платники ПДВ", sea: "СЕА ПДВ — лише для платників ПДВ", smkor: "СМКОР — моніторинг реєстрації ПН, лише для платників ПДВ",
  movement: "Рух товарів будується з ПН/РК — лише для платників ПДВ",
  compliance: "Позитивна податкова історія — критерій СМКОР для платників ПДВ" };
const SYNC = ["erpn", "card", "budget", "sea", "smkor", "reports", "prro"];
export function resolveCapabilities(profile) {
  if (!profile) return { compat: true, sections: {}, sync: SYNC, reports: { vat: true, payroll: true, fin: true }, notYet: [] };
  const vat = profile.vat_status === "registered";
  const sections = {};
  for (const k of ALWAYS) sections[k] = { applicable: true };
  for (const [k, why] of Object.entries(VAT_ONLY)) sections[k] = vat ? { applicable: true } : { applicable: false, reason: why };
  const legal = profile.entity_kind === "legal_person";
  const reports = { vat, payroll: true, fin: legal };
  const notYet = [];
  if (profile.tax_system === "single") notYet.push(`Облік доходів і декларація платника єдиного податку ${profile.single_tax_group} групи`);
  if (profile.entity_kind === "sole_trader") notYet.push("Власний ЄСВ ФОП і розрахунок податків ФОП", "Квартальний Податковий розрахунок ФОП-роботодавця (F0500111, з липня 2026)");
  if (legal && profile.tax_system === "general") notYet.push("Декларація з податку на прибуток");
  if (legal && !vat) notYet.push("Фінзвітність і реквізити без поданих ПН (для ТОВ без ПДВ)");
  return { compat: false, sections, sync: SYNC.filter((k) => k === "card" || k === "budget" || k === "reports" || k === "prro" || vat), reports, notYet };
}

// Поля профілю для людини й розбіжності підтвердженого профілю з карткою ДПС (картка новіша або профіль помилковий)
export const FIELD = { entity_kind: "Хто платник", tax_system: "Система оподаткування", vat_status: "ПДВ" };
export function conflicts(profile, candidate) {
  if (!profile || !candidate) return [];
  const out = [];
  for (const f of ["entity_kind", "vat_status"]) if (candidate[f] && candidate[f] !== profile[f]) out.push({ field: f, card: candidate.evidence?.[f]?.label || candidate[f] });
  if (candidate.tax_system && (candidate.tax_system !== profile.tax_system || (candidate.tax_system === "single" && candidate.single_tax_group !== profile.single_tax_group)))
    out.push({ field: "tax_system", card: candidate.evidence?.tax_system?.label || candidate.tax_system });
  return out;
}

// Короткий опис профілю для людини
const VAT_SHORT = { registered: "платник ПДВ", not_registered: "не платник ПДВ" };
export const describe = (p) => p ? [ENTITY[p.entity_kind], p.tax_system === "single" ? `єдиний податок, ${p.single_tax_group} група` : "загальна система", VAT_SHORT[p.vat_status]].join(" · ") : "";
